/**
 * Tiny, safe Markdown subset for the rule book (`RulesVersionView.bodyMd`): `#`…`######` headings,
 * paragraphs (consecutive lines), `- ` / `* ` / `• ` bullets and inline `**bold**`. Everything is rendered
 * as React text, never as HTML, so the stored text cannot inject markup.
 */
import { Fragment, type ReactNode } from "react";

export type MdBlock =
    | { kind: "heading"; level: number; text: string; id: string }
    | { kind: "paragraph"; text: string }
    | { kind: "list"; items: string[] };

const HEADING = /^(#{1,6})\s+(.*)$/;
const BULLET = /^\s*(?:[-*]|•)\s+(.*)$/;

export function parseMarkdown(md: string): MdBlock[] {
    const blocks: MdBlock[] = [];
    let para: string[] = [];
    let list: string[] | null = null;
    let headings = 0;

    const flush = () => {
        if (para.length) blocks.push({ kind: "paragraph", text: para.join(" ") });
        para = [];
        if (list) blocks.push({ kind: "list", items: list });
        list = null;
    };

    for (const raw of md.replace(/\r\n?/g, "\n").split("\n")) {
        const line = raw.trimEnd();
        if (!line.trim()) {
            flush();
            continue;
        }
        const h = HEADING.exec(line.trim());
        if (h) {
            flush();
            blocks.push({ kind: "heading", level: h[1].length, text: h[2].replace(/\s*#+$/, ""), id: `rule-${++headings}` });
            continue;
        }
        const b = BULLET.exec(line);
        if (b) {
            if (para.length) {
                blocks.push({ kind: "paragraph", text: para.join(" ") });
                para = [];
            }
            (list ??= []).push(b[1]);
            continue;
        }
        if (list) {
            blocks.push({ kind: "list", items: list });
            list = null;
        }
        para.push(line.trim());
    }
    flush();
    return blocks;
}

/** Lines of a short Markdown list or text (e.g. `changeSummary`), bullets stripped. */
export const markdownLines = (md: string | null): string[] =>
    (md ?? "")
        .split(/\r?\n/)
        .map((l) => l.replace(BULLET, "$1").trim())
        .filter(Boolean);

/** Inline `**bold**` as <strong>; everything else stays plain text. */
export const Inline = ({ text }: { text: string }) => {
    const parts = text.split(/\*\*(.+?)\*\*/g);
    return (
        <>
            {parts.map((p, i): ReactNode => (i % 2 === 1 ? <strong key={i} className="font-semibold text-primary">{p}</strong> : <Fragment key={i}>{p}</Fragment>))}
        </>
    );
};
